import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { events, featuredEvent, describeCurrentRelease, isEventPast } from './eventsData';
import { offerings } from './offeringsData';
import MailingListForm from './MailingListForm';
import SubtleAnimalLogo from './SubtleAnimalLogo';
import SnailOnly from './SnailOnly';
import EnquiryForm from './EnquiryForm';
import './App.css';


export default function App() {
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const els = document.querySelectorAll('.fade-in');
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const hero = document.getElementById('home');
    if (!hero) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeaderVisible(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  return (
    <div>

      {/* ── STICKY HEADER (desktop) ── */}
      <header className={`site-header${headerVisible ? ' site-header--visible' : ''}`}>
        <a href="#home" className="site-header-brand" aria-label="Subtle Animal home">
          <SubtleAnimalLogo className="header-logo" />
        </a>
        <nav className="site-header-nav">
          <a href="#about">About</a>
          <a href="#events">Events</a>
          <Link to="/past-events">Past Events</Link>
          <Link to="/foraging-guide">Foraging Guide</Link>
          <a href="#contact">Contact</a>
        </nav>
        <a href="#workshops" className="btn site-header-cta">Private &amp; Group Bookings</a>
      </header>

      {/* ── HERO ── */}
      <section className="hero" id="home" style={{ backgroundImage: 'linear-gradient(to bottom, rgba(42,43,34,0.55) 0%, rgba(42,43,34,0.4) 100%), url(/backgrounds/moss-covered-log.jpg)' }}>
        <div className="hero-nav">
          <a href="#home" className="hero-logo-link" aria-label="Subtle Animal home">
            <SubtleAnimalLogo className="hero-logo" />
          </a>
          <div className="hero-nav-buttons">
            <a href="#workshops" className="btn btn-ghost">Workshops</a>
            {!isEventPast(featuredEvent) && (
              <a href="#retreats" className="btn btn-ghost">Retreats</a>
            )}
            <a href="#about" className="btn btn-ghost">About</a>
            <a href="#contact" className="btn btn-ghost">Contact</a>
            <Link to="/community-partnerships" className="btn btn-ghost">Partnerships</Link>
            <Link to="/foraging-guide" className="btn btn-ghost">Foraging Guide</Link>
            <a href="#workshops" className="btn btn-ghost">Private &amp; Group Bookings</a>
          </div>
        </div>
        <SnailOnly className="snail-watermark" />
        <div className="hero-inner fade-in">
          <h1 className="hero-title">Subtle Animal</h1>
          <p className="hero-eyebrow">Nature Connection · Rewilding · Ancestral Skills</p>
          <div className="hero-cta-group">
            <a href="#events" className="btn btn-ghost hero-cta">Book Upcoming Workshops</a>
            <a href="#workshops" className="btn btn-outline-maroon hero-cta">Private &amp; Group Bookings</a>
          </div>
        </div>
      </section>

      {/* ── RETREATS ── */}
      {!isEventPast(featuredEvent) && (
        <section className="events" id="retreats" style={{ backgroundImage: 'linear-gradient(rgba(240,237,224,0.92), rgba(240,237,224,0.92)), url(/backgrounds/lichen-moss-ground.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <SnailOnly className="snail-watermark" />
          <div className="events-inner">
            <div className="events-header fade-in">
              <p className="section-label section-label--maroon">Retreats</p>
            </div>
            <div className="event-cards" style={{ marginBottom: '2rem' }}>
              <Link to={`/events/${featuredEvent.slug}`} className="event-card event-card--featured event-card--link" style={{ backgroundImage: `url(${featuredEvent.img})` }}>
                <div className="event-card-img event-card-img--banner">
                  <div className="event-card-banner-text">
                    <h2 className="event-card-banner-title">Wild Alignment</h2>
                    <p className="event-card-banner-sub">Three days of movement, craft, and song</p>
                    <p className="event-card-banner-activities">Herbal skincare · Plant printing on fabric · Primal movement · Bush foods · Awareness practices</p>
                  </div>
                </div>
                <div className="event-card-body">
                  <p className="event-card-date">{featuredEvent.date}</p>
                  <h3>{featuredEvent.title}</h3>
                  {featuredEvent.releases && (
                    <p className="event-card-early-bird">{describeCurrentRelease(featuredEvent.releases)}</p>
                  )}
                  <a
                    href={featuredEvent.ticketUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-maroon event-card-book-btn"
                    onClick={e => e.stopPropagation()}
                  >
                    Book Now
                  </a>
                </div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── WORKSHOPS & EVENTS ── */}
      <section className="events" id="events" style={{ backgroundImage: 'linear-gradient(rgba(240,237,224,0.92), rgba(240,237,224,0.92)), url(/backgrounds/lichen-moss-ground.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <SnailOnly className="snail-watermark" />
        <div className="events-inner">
          <div className="events-header fade-in">
            <p className="section-label section-label--maroon">Workshops & Events</p>
          </div>

          {/* Upcoming, bookable dated events */}
          {events.some(ev => !ev.soldOut && !isEventPast(ev)) && (
            <>
              <p className="series-label">Upcoming Dates</p>
              <div className="event-cards" style={{ marginBottom: '2rem' }}>
                {events.filter(ev => !ev.soldOut && !isEventPast(ev)).map(ev => (
                  <Link to={`/events/${ev.slug}`} key={ev.slug} className="event-card event-card--link">
                    <img className="event-card-img" src={ev.img} alt={ev.title} style={ev.imgPosition ? { objectPosition: ev.imgPosition } : undefined} />
                    <div className="event-card-body">
                      <p className="event-card-date">{ev.date}</p>
                      <h3>{ev.title}</h3>
                      <a href={ev.ticketUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-maroon event-card-book-btn" onClick={e => e.stopPropagation()}>Book Now</a>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}

          {/* Workshop catalog */}
          <p id="workshops" className="series-label">Workshops</p>
          <div className="event-cards" style={{ marginBottom: '2rem' }}>
            {offerings.map(o => (
              <Link to={`/workshops/${o.slug}`} key={o.slug} className="event-card event-card--link">
                <img className="event-card-img" src={o.img} alt={o.title} style={o.imgPosition ? { objectPosition: o.imgPosition } : undefined} />
                <div className="event-card-body">
                  <h3>{o.title}</h3>
                  <span className="btn btn-outline-maroon event-card-book-btn">View Workshop</span>
                </div>
              </Link>
            ))}
          </div>

          <div className="events-past-link">
            <Link to="/past-events" className="btn btn-outline-maroon">Past Events</Link>
          </div>
        </div>
      </section>

      {/* ── GROUP SIZES & PRICING ── */}
      <section className="pricing-section" id="pricing" style={{ backgroundImage: 'linear-gradient(rgba(240,237,224,0.92), rgba(240,237,224,0.92)), url(/backgrounds/pb-lichen-grey.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <SnailOnly className="snail-watermark" />
        <div className="pricing-inner">
          <p className="section-label section-label--maroon" style={{ textAlign: 'center' }}>Private &amp; Group Bookings</p>
          <h2 className="pricing-title">Group Sizes &amp; Pricing</h2>
          <p className="pricing-text">
            All sessions include a minimum booking fee. Additional participants are charged per person beyond that base. Group discounts are available – get in touch and we'll put together a tailored quote.
          </p>
          <div className="pricing-table-wrap">
            <table className="pricing-table">
              <thead>
                <tr>
                  <th>Workshop</th>
                  <th>Min. Booking</th>
                  <th>Per Extra Person</th>
                  <th>Max Participants</th>
                </tr>
              </thead>
              <tbody>
                {offerings.map(o => (
                  <tr key={o.slug}>
                    <td>{o.title}</td>
                    <td>{o.pricing.min} {o.pricing.minNote && <span className="pricing-note">{o.pricing.minNote}</span>}</td>
                    <td>{o.pricing.extra} {o.pricing.extraNote && <span className="pricing-note">{o.pricing.extraNote}</span>}</td>
                    <td>{o.pricing.max}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <a href="#group-enquiry" className="btn btn-forest pricing-cta">Get a Custom Quote</a>

          <div className="pricing-enquiry pricing-inner--narrow" id="group-enquiry">
            <h3 className="pricing-title" style={{ fontSize: '1.4rem' }}>Send an Enquiry</h3>
            <p className="pricing-text">
              Tell us about your group – who you are, what you're drawn to, and when you're thinking of gathering. We'll be in touch with a custom proposal.
            </p>
            <EnquiryForm />
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section className="about" id="about" style={{ backgroundImage: 'linear-gradient(to right, rgba(30,38,20,0.75) 0%, rgba(30,38,20,0.65) 100%), url(/backgrounds/who-we-are-bg.jpg)' }}>
        <SnailOnly className="snail-watermark" />
        <div className="about-inner fade-in">
          <div className="about-text">
            <p className="section-label">Who We Are</p>
            <p className="lead">
              Subtle Animal is a nature-connection platform bringing together facilitators with diverse
              skills to offer experiences rooted in relationship with land, with body, and with the
              more-than-human world we belong to.
            </p>
            <p>
              At its heart is a simple but radical reorientation: power <em>with</em> nature, rather than
              power <em>over</em> it. Caring for ourselves and caring for the Earth are not separate acts.
              We are ecological beings. When we tend land, waters, and kin, we are tending ourselves.
            </p>
            <p>
              Through embodied, place-based practice, Subtle Animal explores how reconnection becomes
              practical care. When we learn to eat what the land offers, harvested with care and
              reciprocity, we nourish ourselves while supporting ecological balance. When we slow down
              and listen more closely to seasons, to soil, to our own subtle animal intelligence we
              begin to love more deeply, and from that love, to act. This attention nourishes mental
              wellbeing while strengthening the edible, oxygen-producing, habitat-holding, soil-binding
              systems that sustain all life.
            </p>
            <p>
              The work is grounded in somatic awareness, deep ecology, and relational rewilding.{' '}
              <em>Place is not a backdrop. It is a collaborator.</em>
            </p>
            <a
              href="https://www.instagram.com/subtleanimal/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost about-socials-btn"
            >
              Follow us on Instagram
            </a>
            <div className="about-note">
              Subtle Animal operates on unceded Country, with deep respect and gratitude for the Aboriginal peoples who have lived in relationship with this land for tens of thousands of years.
            </div>
          </div>
        </div>
      </section>

      {/* ── PHILOSOPHY / WHAT WE OFFER ── */}
      <section className="philosophy" style={{ backgroundImage: 'linear-gradient(to right, rgba(30,38,20,0.75) 0%, rgba(30,38,20,0.65) 100%), url(/backgrounds/unfurling-fern.jpg)' }}>
        <SnailOnly className="snail-watermark" />
        <div className="philosophy-inner fade-in">
          <div className="philosophy-text">
            <p className="section-label">What We Offer</p>
            <h2 className="section-title">Workshops, Immersions &amp; Retreats</h2>
            <p>
              Subtle Animal offers workshops, immersions, and retreats across a wide spectrum – from
              councils, festivals, organisations, and corporate wellbeing programs, to communities and
              individuals already deep in the work of nature connection and rewilding. Each offering is
              shaped collaboratively, with sensitivity to context, culture, and Country.
            </p>
            <p>
              To enquire about a private event, group discounts or custom offering, reach us at{' '}
              <a href="mailto:thesubtleanimal@gmail.com">thesubtleanimal@gmail.com</a>
            </p>
            <div className="mailing-list-embed">
              <p className="mailing-list-embed-label">Stay in touch</p>
              <MailingListForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── ILANA ── */}
      <section className="ilana" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/backgrounds/bark-texture.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <SnailOnly className="snail-watermark" />
        <div className="ilana-inner fade-in">
          <div className="ilana-photos">
            <img src="/bio img/person-and-dog-on-rocks.jpg" alt="Ilana and her dog on rocks by the ocean" />
          </div>
          <div className="ilana-text">
            <p className="section-label">Founder & Core Facilitator</p>
            <h2 className="section-title">Ilana Hoffman</h2>
            <p>
              Ilana has spent her life in close conversation with the living world – its creatures, its
              medicines, its older ways of knowing.
            </p>
            <p>
              She trained as a veterinarian and has worked with animals across Australia and beyond, in
              wildlife medicine, rescue, conservation and community care. That work is one thread in a
              wider practice bringing together animal, environmental and human wellbeing. Ilana is a
              forager, herbalist, textile artist, weaver, yoga and meditation teacher, dancer and freediver.
            </p>
            <p>
              Her work doesn't sit neatly in one category. Part science, part craft, part ritual, with a
              good amount of play running through all of it. Practical, hands-in-the-dirt, made to change
              how we perceive ourselves and so how we relate to the living world we belong to.
            </p>
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section className="contact" id="contact" style={{ backgroundImage: 'linear-gradient(rgba(20,30,15,0.6), rgba(20,30,15,0.6)), url(/backgrounds/contact-bg.jpg)' }}>
        <SnailOnly className="snail-watermark" />
        <div className="contact-inner fade-in">
          <p className="section-label">Get in Touch</p>
          <h2 className="section-title">Contact</h2>
          <p className="contact-desc">For enquiries, private bookings, and custom offerings.</p>
          <a href="mailto:thesubtleanimal@gmail.com" className="contact-email">thesubtleanimal@gmail.com</a>
          <a href="https://www.instagram.com/subtleanimal/" target="_blank" rel="noopener noreferrer" className="contact-instagram">
            <svg className="contact-instagram-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
              <circle cx="12" cy="12" r="4"/>
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
            </svg>
            @subtleanimal
          </a>
          <a
            href="https://share.google/GdiHrKqyt9akqpM6n"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-review-btn"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="contact-review-star">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            Leave us a Google Review
          </a>
          <div className="mailing-list-embed mailing-list-embed--contact">
            <p className="mailing-list-embed-label">Join our mailing list</p>
            <MailingListForm />
          </div>
        </div>
      </section>

      {/* ── MOBILE CTA BAR ── */}
      <a href="#events" className="mobile-cta-bar">Book Tickets</a>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer-content">
        <div className="footer-text">
        <p>&copy; 2026 <strong>Subtle Animal</strong> &ndash; Nature connection, rewilding &amp; ancestral skills.</p>
        <p className="footer-phone"><a href="tel:+61417430206">+61 417 430 206</a></p>
<ul className="footer-links">
          <li><a href="#home">Home</a></li>
          <li><a href="#about">About</a></li>
          <li><a href="#events">Events</a></li>
          <li><a href="#contact">Contact</a></li>
          <li>
            <a href="https://www.instagram.com/subtleanimal/" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label="Instagram">
              <svg className="footer-social-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
              </svg>
            </a>
          </li>
          <li>
            <a href="https://www.facebook.com/profile.php?id=61573240501320" target="_blank" rel="noopener noreferrer" className="footer-fb-link" aria-label="Facebook">
              <span className="footer-fb-icon">f</span>
            </a>
          </li>
        </ul>
        </div>
        <div className="footer-map">
          <iframe
            src="https://www.google.com/maps?q=Subtle+Animal&ftid=0x24cae2dc2767c529:0x53cfee3cfb3e5b26&output=embed"
            width="100%"
            height="70"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Subtle Animal on Google Maps"
          />
        </div>
        </div>
        <p className="footer-acknowledgment">
          Subtle Animal operates on unceded Country, with deep respect and gratitude for the Aboriginal peoples who have lived in relationship with this land for tens of thousands of years.
        </p>
      </footer>

    </div>
  );
}
